<script lang="ts" setup>
import { getImagesByPriority } from '@/views/search';
import { computed } from 'vue';
import { Constants } from '@/utilities/constants.ts';
import ProjectBadge from '@/components/ProjectBadge.vue';
import type { GetFeaturedProjectsProjectResponse } from '@/api';

const { index, project } = defineProps<{
  index: number;
  project: GetFeaturedProjectsProjectResponse;
}>();

const images = computed(() => getImagesByPriority(project, 3));

const fetchPriority = computed(() => (index === 0 ? 'high' : 'auto'));
const loadingPriority = computed(() => (index < 2 ? 'eager' : 'lazy'));
const featuredMainSizes = `(max-width: ${Constants.Breakpoints.md}) 60vw, ${Constants.ImageSizes.Medium.Width}px`;
const featuredSmallSizes = `(max-width: ${Constants.Breakpoints.sm}) 40vw, ${Constants.ImageSizes.Small.Width}px`;
</script>

<template>
  <li class="col-span-full pb-2">
    <div class="group relative grid grid-cols-12 gap-4 p-2 md:gap-6 md:p-3">
      <div class="col-span-full grid grid-cols-6 gap-2 sm:col-span-7 md:gap-3 lg:gap-4">
        <!-- Main Image -->
        <div class="aspect-h-2 aspect-w-3 col-span-4 row-span-2 block w-full overflow-hidden rounded-lg">
          <img
            v-if="images[0]"
            :alt="images[0].title"
            :fetchpriority="fetchPriority"
            :height="Constants.ImageSizes.Medium.Height"
            :loading="loadingPriority"
            :sizes="featuredMainSizes"
            :src="images[0].src.resizedToSmall()"
            :srcset="images[0].src.toSrcset()"
            :width="Constants.ImageSizes.Medium.Width"
            class="object-cover"
          />
          <div class="pointer-events-none absolute inset-0 p-2">
            <ProjectBadge :banners="project.banners" :isNew="project.isNew" :isPaid="project.isPaid" :isSold="project.isSold" />
          </div>
          <div class="absolute inset-0">
            <div class="absolute right-0 bottom-0 h-auto w-1/4 overflow-hidden rounded-tl-lg rounded-br-lg border-4 border-gray-700/50">
              <div class="bg-white p-[8px]">
                <img
                  v-if="project.logo"
                  :alt="project.name"
                  :fetchpriority="fetchPriority"
                  :height="Constants.ImageSizes.Small.Height"
                  :loading="loadingPriority"
                  :src="project.logo.resizedToSmall(true)"
                  :width="Constants.ImageSizes.Small.Width"
                  sizes="40vw"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Top-right Image -->
        <div class="col-span-2 flex">
          <img
            v-if="images[1]"
            :alt="images[1].title"
            :fetchpriority="fetchPriority"
            :height="Constants.ImageSizes.Small.Height"
            :loading="loadingPriority"
            :sizes="featuredSmallSizes"
            :src="images[1].src.resizedToSmall()"
            :srcset="images[1].src.toSrcset()"
            :width="Constants.ImageSizes.Small.Width"
            class="h-full w-full rounded-lg object-cover"
          />
        </div>

        <!-- Bottom-right Image -->
        <div class="col-span-2 flex">
          <img
            v-if="images[2]"
            :alt="images[2].title"
            :fetchpriority="fetchPriority"
            :height="Constants.ImageSizes.Small.Height"
            :loading="loadingPriority"
            :sizes="featuredSmallSizes"
            :src="images[2].src.resizedToSmall()"
            :srcset="images[2].src.toSrcset()"
            :width="Constants.ImageSizes.Small.Width"
            class="h-full w-full rounded-lg object-cover"
          />
        </div>
      </div>

      <!-- Text -->
      <div class="col-span-full flex flex-col sm:col-span-5">
        <h3 class="2xs:text-base xs:text-lg block truncate text-sm font-medium tracking-tight text-gray-900">
          {{ project.name }}
        </h3>
        <p class="2xs:text-sm xs:text-base block text-xs font-medium text-gray-500">{{ project.address.city.name }}, {{ project.address.province }}</p>
        <div class="xs:text-sm relative min-h-24 grow overflow-hidden text-xs text-gray-700 after:absolute after:inset-x-0 after:bottom-0 after:h-16 after:bg-linear-to-b after:from-transparent after:to-white after:content-['']">
          <div class="markdown absolute top-0 left-0 text-gray-700" v-html="project.description.toHtmlContent()"></div>
        </div>
      </div>

      <!-- Click -->
      <RouterLink :to="{ name: 'project', params: { slug: project.name.toSlug(), projectId: project.id } }" class="focus-within:ring-ph-green-500 absolute inset-0 cursor-pointer rounded-lg focus-within:ring-2 focus-within:outline-hidden">
        <span class="sr-only">View details for {{ project.name }}</span>
      </RouterLink>
    </div>
  </li>
</template>

<style lang="css" scoped></style>
